import React from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { TrashIcon } from 'lucide-react';
import { PermissionGuard } from '@/features/auth';
import { Badge, Button, LoadingSpinner, Table } from '@/shared/components';
import type { DealerContact } from '../types/dealers.types';

interface Props {
    data: DealerContact[];
    isLoading?: boolean;
    onDelete: (id: string) => void;
    isDeleting?: boolean;
    emptyMessage?: string;
}

export const DealerContactDataTable: React.FC<Props> = ({
    data,
    isLoading = false,
    onDelete,
    isDeleting = false,
    emptyMessage,
}) => {
    if (isLoading) {
        return <LoadingSpinner size="xl" className="py-12" message="Loading..." />;
    }

    const columns: ColumnDef<DealerContact>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            cell: (info) => <span className="font-semibold text-sm">{info.getValue() as string}</span>,
        },
        {
            accessorKey: 'role',
            header: 'Role',
            cell: (info) => <span className="text-sm">{(info.getValue() as string | null) ?? <span className="text-mp-muted">—</span>}</span>,
        },
        {
            accessorKey: 'email',
            header: 'Email',
            cell: (info) => <span className="text-sm">{(info.getValue() as string | null) ?? <span className="text-mp-muted">—</span>}</span>,
        },
        {
            accessorKey: 'phone',
            header: 'Phone',
            cell: (info) => <span className="text-sm">{(info.getValue() as string | null) ?? <span className="text-mp-muted">—</span>}</span>,
        },
        {
            accessorKey: 'notify_agreements',
            header: 'Agreements',
            cell: (info) => info.getValue() ? <Badge size="xs" variant="success">On</Badge> : <Badge size="xs" variant="info">Off</Badge>,
        },
        {
            accessorKey: 'notify_payments',
            header: 'Payments',
            cell: (info) => info.getValue() ? <Badge size="xs" variant="success">On</Badge> : <Badge size="xs" variant="info">Off</Badge>,
        },
        {
            id: 'actions',
            header: '',
            cell: (info) => (
                <PermissionGuard permission={{ resource: 'dealer', action: 'edit' }}>
                    <Button
                        size="none"
                        variant="clear"
                        onClick={() => onDelete(info.row.original.id)}
                        disabled={isDeleting}
                        startIcon={<TrashIcon size={18} className="text-mp-error" />}
                    />
                </PermissionGuard>
            ),
            enableSorting: false,
        },
    ];

    return <Table<DealerContact> data={data} columns={columns} enableSorting={false} emptyMessage={emptyMessage} />;
};
